import { React, useRef, useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { reporteDeeventos } from "../../../store/Calendario/thunkCalendario";
import Swal from "sweetalert2";
import { estadosEvento, hotelesOpciones } from "../../../hook/Arreglos";
import DataTable from "datatables.net-react";
import DT from "datatables.net-dt";
import "datatables.net-bs5";
import "datatables.net-select-dt";
import "datatables.net-select-bs5";
import "datatables.net-responsive-dt";
import "datatables.net-searchpanes-dt";
import "datatables.net-searchpanes-bs5";
import "datatables.net-buttons";
import "datatables.net-buttons-bs5";
import "datatables.net-buttons/js/buttons.html5";
import "jszip";
import "pdfmake";
DataTable.use(DT);
import "./style.css";
import { traerSalones } from "../../../store/cotizaciones/thunkCotizaciones";
import $ from "jquery";

/**
 * Hook personalizado para obtener y gestionar datos de la tabla.
 * @param {string} fechaInicio - Fecha de inicio en formato YYYY-MM-DD.
 * @param {string} fechaFin - Fecha de fin en formato YYYY-MM-DD.
 * @returns {[Array, Function]} Tupla que contiene el array de datos y la función setter.
 */
const extarerTodosLosEventos = (fechaInicio, fechaFin) => {
    const dispatch = useDispatch();
    const [datos, establecerDatos] = useState([]);

    useEffect(() => {
        const obtenerDatos = async () => {
            try {
                // Mostrar alerta de carga
                Swal.fire({
                    title: "Cargando datos...",
                    text: "Por favor espere",
                    allowOutsideClick: false,
                    didOpen: () => {
                        Swal.showLoading();
                    },
                });

                const resultado = await dispatch(reporteDeeventos(fechaInicio, fechaFin));

                establecerDatos(resultado);

                // Cerrar alerta de carga
                Swal.close();
            } catch (error) {
                // Manejar error y cerrar alerta de carga
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: "Error al cargar los datos",
                });
            }
        };
        obtenerDatos();
    }, [dispatch, fechaInicio, fechaFin]);

    return [datos, establecerDatos];
};

/**
 * Hook personalizado para obtener y gestionar datos de salones.
 * @returns {[Array, Function]} Tupla que contiene el array de salones y la función setter.
 * @description Obtiene los salones del hotel con ID 200
 */
const extarerTodosLosSalones = () => {
    const dispatch = useDispatch();
    const [datos, establecerDatos] = useState([]);

    useEffect(() => {
        const obtenerDatos = async () => {
            try {
                const resultado = await dispatch(traerSalones({ idHotel: "200" }));
                establecerDatos(resultado);

                // Cerrar alerta de carga
                Swal.close();
            } catch (error) {
                console.log(error);
            }
        };
        obtenerDatos();
    }, [dispatch]);

    return [datos, establecerDatos];
};

/**
 * Array global para almacenar los nombres de los salones
 * @type {Array<{id_salon: number, nombre_salon: string}>}
 */
const salonesNombre = [];

/**
 * Función para formatear la fecha
 * @param {string} dateString - Fecha en formato YYYY-MM-DD
 * @returns {string} Fecha formateada
 */
const formatDate = (dateString) => {
    // Formatear la fecha
    const date = new Date(dateString);
    // Opciones de formateo
    const options = { weekday: "long", year: "numeric", month: "short", day: "numeric" };
    // Devolver la fecha formateada
    return date.toLocaleDateString("es-ES", options);
};

/**
 * Mapeo de días de la semana a sus iniciales
 * @type {Object.<string, string>}
 */
const DAYS_INITIALS = {
    Monday: "M",
    Tuesday: "K",
    Wednesday: "W",
    Thursday: "R",
    Friday: "F",
    Saturday: "S",
    Sunday: "D",
};

/**
 * Función para obtener la inicial del día
 * @param {string} dateString - Fecha en formato YYYY-MM-DD
 * @returns {string} Inicial del día
 */
const getDayInitial = (dateString) => {
    const date = new Date(dateString);
    const day = date.toLocaleDateString("en-US", { weekday: "long" });
    return DAYS_INITIALS[day] || "N/A";
};

/**
 * Función para obtener el período AM / PM
 * @param {string} dateString - Fecha en formato YYYY-MM-DD
 * @returns {string} Período AM / PM
 */
const getAMPM = (dateString) => {
    // Formatear la fecha
    const date = new Date(dateString);
    // Obtener las horas
    const hours = date.getHours();
    // Devolver el período AM / PM
    return hours < 12 ? "AM" : "PM";
};

/**
 * Definiciones de columnas para la tabla de reporte de eventos.
 * @type {Array<{title: string, data: string, className: string, render?: Function}>}
 * @property {string} title - Título de la columna
 * @property {string} data - Campo de datos a mostrar
 * @property {string} className - Clase CSS para la columna
 * @property {Function} render - Función opcional para personalizar el renderizado
 */
const columnasTablaEventos = [
    // Columnas de la tabla

    // Nombre del hotel columna 0
    {
        title: "Hotel",
        data: "id_hotel",
        render: (data) => hotelesOpciones.find((h) => h.value === data?.toString())?.label || "N/A",
    },
    // Fecha columna 1
    {
        title: "Fecha",
        data: "fech_Inicio_evento",
        render: formatDate,
    },
    // Estado del evento columna 2
    {
        title: "Estado del evento",
        data: "estado_evento",
        render: function (data, row) {
            if (row.status_evento === 2) {
                return `<span style="background-color: red; color: white; padding: 4px 8px; border-radius: 4px; display: inline-block;">Perdido</span>`;
            }
            
            const estado = estadosEvento.find(estado => estado.id_estado === parseInt(data));
            if (estado) {
                return `<span style="background-color: ${estado.color_estado}; color: black; padding: 4px 8px; border-radius: 4px; display: inline-block;">${estado.nombre_estado}</span>`;
            }
            
            return `<span style="padding: 4px 8px; display: inline-block;">N/A</span>`;
        },
    },

    // Nombre del hotel columna 3
    {
        title: "Nombre Evento",
        data: "nombre_evento",
        render: (data) => data || "N/A",
    },
    // Nombre del salon columna 4
    {
        title: "Salón",
        data: "id_salon",
        render: (data) => salonesNombre.find((s) => s.id_salon === parseInt(data, 10))?.nombre_salon || "N/A",
    },

    // Día L - D columna 5
    {
        title: "Día L - D",
        data: "fech_Inicio_evento",
        render: getDayInitial,
    },

    // Período AM / PM columna 6
    {
        title: "Período AM / PM",
        data: "fech_Inicio_evento",
        render: getAMPM,
    },

    // Cliente columna 7
    {
        title: "Cliente",
        data: "nombre_cliente",
        render: (data) => (data === "0" ? "N/A" : data),
    },

    // Administrador columna 8
    {
        title: "Administrador",
        data: "nombres_admins",
        render: (data) => data || "N/A",
    },

    // Pax columna 9
    {
        title: "Pax",
        data: "cantidad_evento",
        render: (data) => data || "N/A",
    },

    // Ingreso esperado columna 10
    {
        title: "Ingreso esperado",
        data: "ingresos_evento",
        render: (data) => data || "N/A",
    },

    // Tipo de evento columna 11
    {
        title: "Tipo de evento",
        data: "tipo_evento",
        render: (data) => data || "N/A",
    },

    // Empresa columna 12
    {
        title: "Empresa",
        data: "nombre_empresa",
        render: (data) => data || "N/A",
    },
].map((col) => ({
    ...col,
    className: col.className || "text-left",
}));

/**
 * Hook personalizado para opciones de tabla con características avanzadas y responsivas.
 * @param {Array<number>} contenido - Índices de columnas para paneles de búsqueda.
 * @returns {Object} Opciones de configuración de la tabla.
 * @property {boolean} stateSave - Guarda el estado de la tabla
 * @property {string} dom - Configuración del DOM de DataTables
 * @property {Array} order - Orden inicial de la tabla
 * @property {boolean} paging - Habilita la paginación
 * @property {Object} searchPanes - Configuración de paneles de búsqueda
 */
export const usarOpcionesTabla = (contenido) => {
    return {
        stateSave: true,
        dom: "lPBfrtip",
        order: [[2, "desc"]],
        paging: true,
        pageLength: 10,
        lengthMenu: [
            [10, 25, 50, 200, -1],
            [10, 25, 50, 200, "Todos"],
        ],
        responsive: true,
        buttons: [
            {
                extend: "excel",
                text: "Exportar a Excel",
                className: "btn btn-success",
                title: `Reporte de Eventos ${new Date().toLocaleDateString()}`,
                messageTop: "Reporte generado desde el sistema CRM Hotel",
                messageBottom: `Fecha de generación: ${new Date().toLocaleString()}`,
                exportOptions: {
                    columns: ":visible",
                    format: {
                        body: function (data, row, column, node) {
                            return data.replace(/(&nbsp;|<([^>]+)>)/gi, "");
                        },
                    },
                },
                autoFilter: true,
                customize: function (xlsx) {
                    var sheet = xlsx.xl.worksheets["sheet1.xml"];

                    $("row:first c", sheet).attr("s", "2");

                    var cols = $("col", sheet);
                    cols.each(function (i) {
                        $(this).attr("width", 20);
                    });

                    $("row:first c", sheet).attr("s", "50");
                    var lastRow = $("row", sheet).length;

                    $("row c", sheet).attr("s", "25");

                    var nameRow = $("row", sheet)[0];
                    nameRow.setAttribute("frozen", "true");
                    nameRow.setAttribute("freezePane", "true");
                },
            },
            {
                extend: "excel",
                text: "Excel Simple",
                className: "btn btn-primary",
                title: "Reporte Simple",
                exportOptions: {
                    columns: ":visible",
                },
            },
        ],
        processing: true,
        searchPanes: {
            show: true,
            initCollapsed: true,
            cascadePanes: true,
            controls: true,
            collapse: true,
            dtOpts: {
                select: {
                    style: "multi",
                },
                count: {
                    show: false,
                },
            },
            layout: "columns-3",
        },
        columnDefs: [{ targets: contenido, searchPanes: { show: true } }],
        language: {
            search: "Buscar:",
            lengthMenu: "Mostrar _MENU_ registros por página",
            zeroRecords: "No se encontraron resultados",
            info: "Mostrando página _PAGE_ de _PAGES_",
            infoEmpty: "No hay registros disponibles",
            infoFiltered: "(filtrado de _MAX_ registros totales)",
        },
    };
};

/**
 * Componente presentacional puro para renderizar el DataTable.
 * @component
 * @param {Object} props - Propiedades del componente
 * @param {Array} props.datos - Datos a mostrar en la tabla
 * @param {Array} props.columnas - Configuración de columnas
 * @param {Object} props.opciones - Opciones básicas de la tabla
 * @param {Object} props.referenciaTabla - Ref para acceder al DOM de la tabla
 * @param {Object} props.opcionesTabla - Opciones avanzadas incluyendo callbacks
 * @returns {JSX.Element} Tabla renderizada con DataTables
 */
const TablaEventos = ({ datos, columnas, opciones, referenciaTabla, opcionesTabla }) => (
    <div className="table-responsive">
        <div style={{ margin: "0", padding: "0.5rem" }}>
            <DataTable
                data={datos}
                ref={referenciaTabla}
                className="table table-striped table dt-responsive w-100 display text-left"
                options={opcionesTabla}
                columns={columnas}
            />
        </div>
    </div>
);

TablaEventos.propTypes = {
    datos: PropTypes.array.isRequired,
    columnas: PropTypes.array.isRequired,
    opciones: PropTypes.object.isRequired,
    referenciaTabla: PropTypes.object.isRequired,
};

/**
 * Función de utilidad para cálculos de fechas.
 * @returns {{primerDia: string, ultimoDia: string}} Objeto con fechas formateadas
 * @property {string} primerDia - Primer día del mes actual en formato YYYY-MM-DD
 * @property {string} ultimoDia - Último día del mes actual en formato YYYY-MM-DD
 */
const obtenerFechasPorDefecto = () => {
    const ahora = new Date();
    const primerDia = new Date(ahora.getFullYear(), ahora.getMonth(), 1).toISOString().split("T")[0];
    const ultimoDia = new Date(ahora.getFullYear(), ahora.getMonth() + 1, 0).toISOString().split("T")[0];
    return { primerDia, ultimoDia };
};

/**
 * Componente contenedor principal para el reporte de eventos.
 * @component
 * @description Implementa los principios SOLID y gestiona:
 * - Estado de fechas de filtro
 * - Carga de datos de eventos
 * - Carga de datos de salones
 * - Renderizado de tabla con DataTables
 * @returns {JSX.Element} Interfaz completa del reporte de eventos
 */
const ReporteEventos = () => {
    /**
     * @type {React.RefObject} Referencia a la tabla para manipulación del DOM
     */
    const referenciaTabla = useRef(null);

    /**
     * @type {string} Fecha de inicio para filtrar eventos
     */
    const { primerDia, ultimoDia } = obtenerFechasPorDefecto();
    const [inputStartDate, setInputStartDate] = useState(primerDia);
    const [inputEndDate, setInputEndDate] = useState(ultimoDia);
    const [datosTabla] = extarerTodosLosEventos(inputStartDate, inputEndDate);
    const [salones] = extarerTodosLosSalones();

    // Asegúrate de limpiar el array antes de agregar nuevos elementos
    salonesNombre.length = 0; // Limpiar el array

    // Agregar los salones al array salonesNombre
    salones.forEach((salon) => salonesNombre.push(salon));

    // Opciones de tabla con manejo de clic en fila
    const opcionesTabla = {
        ...usarOpcionesTabla([0,  2,3,4,5,6,7,8,11]),
        rowCallback: function (fila, datos) {
            fila.addEventListener("click", () => manejarAbrirModal(datos));
        },
    };

    useEffect(() => {
        if (referenciaTabla.current && typeof referenciaTabla.current.DataTable === "function") {
            const tabla = referenciaTabla.current.DataTable();
            tabla.clear();
            tabla.rows.add(datosTabla);
            tabla.columns.adjust().draw();
        }
    }, [datosTabla]);

    // Agregar estadísticas básicas
    const estadisticas = {
        totalEventos: datosTabla.length,
        eventosPorHotel: datosTabla.reduce((acc, curr) => {
            acc[curr.id_hotel] = (acc[curr.id_hotel] || 0) + 1;
            return acc;
        }, {}),
        eventosPorSalon: datosTabla.reduce((acc, curr) => {
            acc[curr.id_salon] = (acc[curr.id_salon] || 0) + 1;
            return acc;
        }, {}),
    };

    return (
        <div className="card">
            <div className="card-header">
                <h5>Reporte de Eventos</h5>
            </div>
            <div className="card-body border-top">
                <div className="row g-4">
                    <div className="col-md-6">
                        <div className="form-floating mb-0">
                            <input type="date" className="form-control" value={inputStartDate} onChange={(e) => setInputStartDate(e.target.value)} />
                            <label htmlFor="startDate">Fecha de inicio de filtro</label>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-floating mb-0">
                            <input type="date" className="form-control" value={inputEndDate} onChange={(e) => setInputEndDate(e.target.value)} />
                            <label htmlFor="endDate">Fecha de final de filtro</label>
                        </div>
                    </div>
                </div>
            </div>
            <TablaEventos
                datos={datosTabla}
                columnas={columnasTablaEventos}
                opciones={opcionesTabla}
                referenciaTabla={referenciaTabla}
                opcionesTabla={opcionesTabla}
            />
        </div>
    );
};

export default ReporteEventos;
